import { Table } from 'antd'
import { TablePaginationConfig } from 'antd/es/table'
import { ColumnsType } from 'antd/lib/table'
import Title from 'antd/lib/typography/Title'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { TABLE_MAX_HEIGHT } from '@/constants/customer'
import { WashingMethod, WashingMethodCustomer, WashingMethodDetergent } from '@/types'
import { formatJapaneseNumber } from '@/utils/formatPrice'

import { CustomRowTable } from './styles'

type Props = {
  dataSource: WashingMethodDetergent[] | undefined
  washingMethod: WashingMethod | undefined
  getWashingMethodDetailLoading: boolean
  onChange: (config: TablePaginationConfig) => void
  customers: WashingMethodCustomer[] | undefined
}

const WashingMethodDetailTable = ({
  dataSource,
  getWashingMethodDetailLoading,
  onChange,
  washingMethod,
  customers,
}: Props) => {
  const { t } = useTranslation()
  const [customerData, setCustomerData] = useState<WashingMethodCustomer[]>([])
  const [detergentData, setDetergentData] = useState<WashingMethodDetergent[]>([])

  useEffect(() => {
    if (customers) {
      setCustomerData(customers.filter((value) => value.customer))
    }
    if (dataSource) {
      setDetergentData(dataSource.filter((data) => data.quantity > 0))
    }
  }, [customers, dataSource])

  const methodColumns: ColumnsType<WashingMethod> = [
    {
      title: t('components.pages.washingMethods.list.table.no'),
      dataIndex: 'code',
      key: 'code',
      width: 200,
      render: (id: number) => {
        return <CustomRowTable type="right">{id}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-tank',
    },
    {
      title: t('components.pages.washingMethods.list.table.name'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => {
        return <CustomRowTable type="left">{name}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-tank',
    },
    {
      title: t('components.pages.washingMethods.list.table.volume'),
      dataIndex: 'volume',
      key: 'volume',
      render: (time: number) => {
        return <CustomRowTable type="right">{time}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-tank',
    },
    {
      title: t('components.pages.washingMethods.list.table.time'),
      dataIndex: 'time',
      key: 'time',
      render: (time: number) => {
        return <CustomRowTable type="right">{time}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-tank',
    },
    {
      title: t('components.pages.washingMethods.list.table.cost'),
      dataIndex: 'cost',
      key: 'cost',
      render: (cost: number) => {
        return <CustomRowTable type="right">{cost ? formatJapaneseNumber(cost) : 0}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-tank',
    },
    {
      title: t('components.pages.washingMethods.list.table.water'),
      dataIndex: 'waterQuantity',
      key: 'waterQuantity',
      render: (waterQuantity: number) => {
        return <CustomRowTable type="right">{formatJapaneseNumber(waterQuantity)}</CustomRowTable>
      },
      align: 'center' as const,
      className: 'nowrap-tank',
    },
  ]

  const methodData = washingMethod ? [washingMethod] : []

  const columns: ColumnsType<WashingMethodDetergent> = [
    {
      title: t('components.pages.washingMethods.detail.table.detergentName'),
      dataIndex: ['detergent', 'name'],
      key: 'name',
      render: (name: string) => {
        return <CustomRowTable type="left">{name}</CustomRowTable>
      },
      align: 'center' as const,
    },
    {
      title: t('components.pages.washingMethods.detail.table.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      render: (quantity: number) => {
        return <CustomRowTable type="right">{formatJapaneseNumber(quantity)}</CustomRowTable>
      },
      align: 'center' as const,
    },
  ]

  const customerColumns: ColumnsType<WashingMethodCustomer> = [
    {
      title: t('components.pages.washingMethods.detail.table.customerCode'),
      dataIndex: ['customer', 'code'],
      key: 'code',
      align: 'center' as const,
    },
    {
      title: t('components.pages.washingMethods.detail.table.customerName'),
      dataIndex: ['customer', 'name'],
      key: 'name',
      render: (name: string) => {
        return <CustomRowTable type="left">{name}</CustomRowTable>
      },
      align: 'center' as const,
    },
  ]

  return (
    <>
      <Title level={4}>{t('components.pages.washingMethods.detail.table.method')}</Title>
      <Table
        rowKey="name"
        bordered
        dataSource={methodData}
        columns={methodColumns}
        pagination={false}
        scroll={{ x: 'max-content', y: TABLE_MAX_HEIGHT }}
      />
      <Title level={4}>{t('components.pages.washingMethods.detail.table.detergentUsed')}</Title>
      <Table
        rowKey="id"
        bordered
        dataSource={detergentData}
        columns={columns}
        loading={getWashingMethodDetailLoading}
        onChange={onChange}
        pagination={false}
        scroll={{ x: 'max-content', y: TABLE_MAX_HEIGHT }}
      />
      <Title level={4}>{t('components.pages.washingMethods.detail.table.customer')}</Title>
      <Table
        rowKey="name"
        bordered
        dataSource={customerData}
        columns={customerColumns}
        pagination={false}
        scroll={{ x: 'max-content', y: TABLE_MAX_HEIGHT }}
      />
    </>
  )
}

WashingMethodDetailTable.displayName = 'WashingMethodDetailTable'
export default WashingMethodDetailTable
